import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import UserLayout from './components/Layout/UserLayout';
import AdminLayout from './components/Layout/AdminLayout';
import FiscalLayout from './components/Layout/FiscalLayout';

// Auth Components
import UserLogin from './components/Auth/UserLogin';
import AdminLogin from './components/Auth/AdminLogin';

// User Pages
import UserDashboard from './pages/User/Dashboard';
import UserInvoices from './pages/User/Invoices';
import CreateInvoice from './pages/User/CreateInvoice';
import InvoiceDetail from './pages/User/InvoiceDetail';
import UserSettings from './pages/User/Settings';
import UserProducts from './pages/User/Products';
import UserClients from './pages/User/Clients';
import UserCredits from './pages/User/Credits';
import UserNominas from './pages/User/Nominas';
import CreateNomina from './pages/User/CreateNomina';
import UserEmployees from './pages/User/Employees';
import CobranzaPPD from './pages/User/CobranzaPPD';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminPlans from './pages/Admin/Plans';
import AdminStats from './pages/Admin/Stats';
import AdminErrorLogs from './pages/Admin/ErrorLogs';
import AdminFiscalAdmins from './pages/Admin/FiscalAdmins';
import AdminEfirmaDevices from './pages/Admin/EfirmaDevices';
import UserTaxCalculation from './pages/User/TaxCalculation';
import AutofacturaConfigs from './pages/User/AutofacturaConfigs';
import AutofacturaPortal from './pages/Public/AutofacturaPortal';
import EfirmaConsentPortal from './pages/Public/EfirmaConsentPortal';

// Fiscal Pages
import FiscalCartera from './pages/Fiscal/Cartera';
import FiscalAgenda from './pages/Fiscal/Agenda';
import FiscalHoy from './pages/Fiscal/Hoy';

// Componente para proteger rutas de usuario
const UserProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Componente para proteger rutas de admin
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, isFiscalAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  if (!isAdmin) {
    // Bifurcación tras /admin/login: un fiscal-admin va a su propio panel,
    // no al de Admin ni al de cliente.
    return <Navigate to={isFiscalAdmin ? '/fiscal/hoy' : '/login'} />;
  }

  return children;
};

/**
 * Panel del fiscal-admin. Admite fiscal-admin y Admin, por la jerarquía
 * fiscal-admin ⊂ Admin.
 *
 * Deniega por omisión: a diferencia de ScreenProtectedRoute, la ausencia del
 * perfil nunca concede acceso.
 */
const FiscalProtectedRoute = ({ children }) => {
  const { isAuthenticated, isStaffFiscal, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  if (!isStaffFiscal) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const ScreenProtectedRoute = ({ screen, children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  if (screen === 'dashboard') {
    return children;
  }

  const allowedScreens = Array.isArray(user?.allowed_screens) ? user.allowed_screens : null;
  const hasAccess = !allowedScreens || allowedScreens.includes(screen);

  return hasAccess ? children : <Navigate to="/dashboard" replace />;
};

/**
 * Bloquea rutas de creación/edición para el perfil visor (plan read_only). Un
 * visor que llegue por URL directa se redirige a `redirectTo`, para no depender
 * solo de ocultar botones. El backend igual rechaza la mutación (403).
 */
const ReadOnlyBlockedRoute = ({ redirectTo = '/dashboard', children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <div>Cargando...</div>;
  }
  const isStaff = user?.level === 'Admin' || user?.staff_role === 'fiscal-admin';
  const isReadOnly = !isStaff && !!user?.read_only;
  return isReadOnly ? <Navigate to={redirectTo} replace /> : children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Rutas públicas */}
          <Route path="/login" element={<UserLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/autofactura/:token" element={<AutofacturaPortal />} />
          {/* Autorización de e.firma: público y sin login, el titular puede no
              haber entrado nunca al sistema */}
          <Route path="/autorizar-efirma/:token" element={<EfirmaConsentPortal />} />
          
          {/* Rutas de usuario protegidas */}
          <Route
            path="/"
            element={
              <UserProtectedRoute>
                <UserLayout />
              </UserProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" />} />
            <Route
              path="dashboard"
              element={
                <ScreenProtectedRoute screen="dashboard">
                  <UserDashboard />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="invoices"
              element={
                <ScreenProtectedRoute screen="invoices">
                  <UserInvoices />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="invoices/create"
              element={
                <ScreenProtectedRoute screen="invoices">
                  <ReadOnlyBlockedRoute redirectTo="/invoices">
                    <CreateInvoice />
                  </ReadOnlyBlockedRoute>
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <ScreenProtectedRoute screen="invoices">
                  <InvoiceDetail />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="products"
              element={
                <ScreenProtectedRoute screen="products">
                  <UserProducts />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="clients"
              element={
                <ScreenProtectedRoute screen="clients">
                  <UserClients />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="employees"
              element={
                <ScreenProtectedRoute screen="employees">
                  <UserEmployees />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ScreenProtectedRoute screen="settings">
                  <UserSettings />
                </ScreenProtectedRoute>
              }
            />
            {/* Nueva ruta de Créditos */}
            <Route
              path="credits"
              element={
                <ScreenProtectedRoute screen="credits">
                  <UserCredits />
                </ScreenProtectedRoute>
              }
            />
            {/* Nueva ruta de Nóminas */}
            <Route
              path="nominas"
              element={
                <ScreenProtectedRoute screen="nominas">
                  <UserNominas />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="nominas/create"
              element={
                <ScreenProtectedRoute screen="nominas">
                  <ReadOnlyBlockedRoute redirectTo="/nominas">
                    <CreateNomina />
                  </ReadOnlyBlockedRoute>
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="tax-calculation"
              element={
                <ScreenProtectedRoute screen="tax-calculation">
                  <UserTaxCalculation />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="cobranza/ppd"
              element={
                <ScreenProtectedRoute screen="cobranza-ppd">
                  <CobranzaPPD />
                </ScreenProtectedRoute>
              }
            />
            <Route
              path="autofactura-configs"
              element={
                <ScreenProtectedRoute screen="autofactura-configs">
                  <AutofacturaConfigs />
                </ScreenProtectedRoute>
              }
            />
          </Route>

          {/* Rutas de admin protegidas */}
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="stats" element={<AdminStats />} />
            <Route path="fiscal-admins" element={<AdminFiscalAdmins />} />
            <Route path="efirma" element={<AdminEfirmaDevices />} />
            <Route path="error-logs" element={<AdminErrorLogs />} />
          </Route>

          {/* Panel del fiscal-admin (también accesible por Admin) */}
          <Route
            path="/fiscal"
            element={
              <FiscalProtectedRoute>
                <FiscalLayout />
              </FiscalProtectedRoute>
            }
          >
            {/* "Hoy" es la entrada: responde "cómo va el mes" de un vistazo, que
                es lo que se consulta desde el celular. La agenda es el trabajo. */}
            <Route index element={<Navigate to="/fiscal/hoy" />} />
            <Route path="hoy" element={<FiscalHoy />} />
            <Route path="agenda" element={<FiscalAgenda />} />
            <Route path="cartera" element={<FiscalCartera />} />
          </Route>

          {/* Ruta por defecto */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
